import React, { Children } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Check, X } from 'lucide-react';
import { BlueprintGrid } from '../components/BlueprintGrid';
export function Home() {
  const fadeIn = {
    hidden: {
      opacity: 0,
      y: 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.22, 1, 0.36, 1]
      }
    }
  };
  const stagger = {
    visible: {
      transition: {
        staggerChildren: 0.1
      }
    }
  };
  return <main className="w-full" data-id="element-413">
      {/* 1) Hero */}
      <section className="relative px-6 pt-36 pb-28 md:pb-32 bg-[#1a2838] overflow-hidden" data-id="element-414">
        {/* Architectural background — sparse vertical rules for structure */}
        <div className="absolute inset-0 pointer-events-none" data-id="element-415">
          <div className="absolute left-[12%] top-0 bottom-0 w-px bg-white/[0.04]" data-id="element-416" />
          <div className="absolute left-[50%] top-0 bottom-0 w-px bg-white/[0.03]" data-id="element-417" />
          <div className="absolute right-[12%] top-0 bottom-0 w-px bg-white/[0.04]" data-id="element-418" />
          <div className="absolute top-[45%] left-[8%] right-[8%] h-px bg-white/[0.04]" data-id="element-419" />
        </div>
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_70%_50%_at_30%_45%,rgba(139,168,159,0.05),transparent)]" data-id="element-420" />

        <div className="relative w-full max-w-5xl mx-auto z-10 pt-6 md:pt-10" data-id="element-421">
          <div className="max-w-3xl" data-id="element-422">
            <motion.h1 initial={{
            opacity: 0,
            y: 18
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.7,
            delay: 0.15,
            ease: [0.22, 1, 0.36, 1]
          }} className="text-[2rem] sm:text-[2.5rem] md:text-[3rem] lg:text-[3.5rem] font-serif font-semibold mb-8 leading-[1.12] tracking-[-0.02em]" data-id="element-423">
              <span className="text-white/80" data-id="element-424">Growth is working.</span>
              <br data-id="element-425" />
              <span className="text-white" data-id="element-426">
                But you are not capturing all the revenue moving through the
                business.
              </span>
            </motion.h1>

            <motion.div initial="hidden" animate="visible" variants={{
            hidden: {
              opacity: 0
            },
            visible: {
              opacity: 1,
              transition: {
                delayChildren: 0.3,
                staggerChildren: 0.05
              }
            }
          }} className="max-w-[540px] mb-8" data-id="element-427">
              {/* Problem — the cause (staggered list) */}
              <div className="border-l border-seaglass/25 pl-4 py-1 space-y-2.5 mb-6" data-id="element-428">
                {['The wrong customers get sold.', 'Leads die in the middle.', 'Handoffs break.', 'Delivery cleans up bad promises.', 'Expansion gets missed.'].map((symptom, i) => <motion.p key={i} variants={{
                hidden: {
                  opacity: 0,
                  x: -10
                },
                visible: {
                  opacity: 1,
                  x: 0,
                  transition: {
                    duration: 0.5,
                    ease: [0.22, 1, 0.36, 1]
                  }
                }
              }} className="text-[15px] text-white/60 font-medium" data-id="element-429">
                    {symptom}
                  </motion.p>)}
              </div>

              {/* Divider — visual pivot between cause and effect */}
              <motion.div variants={{
              hidden: {
                opacity: 0,
                scaleX: 0
              },
              visible: {
                opacity: 1,
                scaleX: 1,
                transition: {
                  duration: 0.5
                }
              }
            }} className="w-10 h-px bg-seaglass/50 origin-left mb-6" data-id="element-430" />

              {/* Response — the effect */}
              <motion.p variants={{
              hidden: {
                opacity: 0,
                y: 10
              },
              visible: {
                opacity: 1,
                y: 0,
                transition: {
                  duration: 0.6,
                  ease: [0.22, 1, 0.36, 1]
                }
              }
            }} className="text-[15px] md:text-[16px] text-white/75 leading-[1.75]" data-id="element-431">
                We help companies find where revenue gets stuck, lost, or missed
                across the journey, then redesign the right parts in the right
                order so more of the right revenue converts, sticks, and
                expands.
              </motion.p>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            y: 12
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            delay: 0.45,
            ease: [0.22, 1, 0.36, 1]
          }} className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6" data-id="element-432">
              <Link to="/honest-conversation" className="inline-flex items-center space-x-3 px-10 py-[18px] bg-sand text-navy text-[15px] font-bold tracking-[0.01em] hover:bg-white transition-all duration-300 w-fit" data-id="element-433">
                <span data-id="element-434">Book an Honest Conversation</span>
                <ArrowRight className="w-4 h-4" data-id="element-435" />
              </Link>
              <span className="text-[13px] text-white/45 font-medium" data-id="element-436">
                45 minutes. No pitch. Just clarity.
              </span>
            </motion.div>
          </div>
        </div>
      </section>

      {/* 2) Recognition */}
      <section className="py-24 px-6 bg-paper grain" data-id="element-437">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={stagger} className="max-w-6xl mx-auto" data-id="element-438">
          <div className="max-w-3xl" data-id="element-439">
            <motion.div variants={fadeIn} data-id="element-440">
              <h2 className="text-4xl md:text-5xl font-serif font-semibold text-navy mb-6 leading-tight" data-id="element-441">
                What looks like a sales problem often isn&apos;t.
              </h2>

              <div className="space-y-4 text-body text-text" data-id="element-442">
                <p data-id="element-443">
                  A lot of companies are generating demand and still not
                  capturing what they should from it.
                </p>
                <p data-id="element-444">
                  What looks like a sales issue, an operations issue, or a
                  customer success issue is often a breakdown somewhere between
                  demand and delivery.
                </p>
                <p data-id="element-445">
                  The real bottleneck rarely lives neatly inside one department.
                  It usually lives in the seams — where handoffs break, promises
                  drift, opportunities go unseen, and the signal is not clear
                  enough to move with confidence.
                </p>
                <p data-id="element-446">That usually shows up when:</p>
              </div>

              <ul className="space-y-3 pl-1 mt-8 mb-8" data-id="element-447">
                {['the wrong customers get sold', 'leads stall in the middle', 'follow-up dies in handoffs', 'delivery inherits bad promises', 'churn starts upstream', 'expansion gets missed', 'pricing or packaging no longer fits the path customers are actually moving through', 'teams keep working around friction instead of removing it'].map((item, i) => <li key={i} className="flex items-start space-x-3" data-id="element-448">
                    <span className="w-1.5 h-1.5 rounded-full bg-seaglass flex-shrink-0 mt-2.5" data-id="element-449" />
                    <span className="text-base text-text font-medium" data-id="element-450">
                      {item}
                    </span>
                  </li>)}
              </ul>

              <p className="text-lg font-semibold text-navy" data-id="element-451">
                The visible symptom is usually not where the real problem lives.
              </p>
            </motion.div>
          </div>
        </motion.div>
      </section>

      {/* Revenue Journey Graphic */}
      <section className="py-16 px-6 bg-navy" data-id="element-452">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={fadeIn} className="max-w-6xl mx-auto" data-id="element-453">
          <p className="eyebrow text-white/40 mb-6 text-center" data-id="element-454">
            The Revenue Journey
          </p>
          <img src="https://cdn.magicpatterns.com/uploads/4XKEhzsa5vVcCQuW3xH9kA/Blueprint_Anatomy_(16_9).png" alt="Revenue journey diagram showing value leaking at each stage — from first interest through qualification, conversion, handoff, delivery, retention, and expansion to customer lifetime value" className="w-full h-auto" data-id="element-455" />
        </motion.div>
      </section>

      {/* 3) What we actually change */}
      <section className="py-24 px-6 bg-paper-dark" data-id="element-456">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={stagger} className="max-w-6xl mx-auto" data-id="element-457">
          <div className="max-w-3xl" data-id="element-458">
            <motion.div variants={fadeIn} className="mb-10" data-id="element-459">
              <h2 className="text-4xl md:text-5xl font-serif font-semibold text-navy mb-6 leading-tight" data-id="element-460">
                What we actually change
              </h2>
            </motion.div>

            <motion.div variants={fadeIn} className="space-y-6 text-body text-text" data-id="element-461">
              <p data-id="element-462">We do not redesign everything.</p>

              <p data-id="element-463">
                We find the point where better structure will create the most
                leverage, then redesign the right parts around it in the right
                order.
              </p>

              <p data-id="element-464">That can include:</p>

              <ul className="space-y-3 pl-1" data-id="element-465">
                {['qualification and fit', 'routing and handoffs', 'conversion logic', 'promise-to-delivery alignment', 'churn drivers that start upstream', 'retention and expansion logic', 'pricing or packaging that no longer fits', 'decision rules and exceptions', 'workflow and team enablement', 'the visibility needed to trust what is happening'].map((item, i) => <li key={i} className="flex items-start space-x-3" data-id="element-466">
                    <span className="w-1.5 h-1.5 rounded-full bg-seaglass flex-shrink-0 mt-2.5" data-id="element-467" />
                    <span className="text-text" data-id="element-468">{item}</span>
                  </li>)}
              </ul>

              <div className="space-y-3 pt-2" data-id="element-469">
                <p data-id="element-470">This is not a giant overhaul.</p>
                <p data-id="element-471">It is not more process for the sake of process.</p>
                <p data-id="element-472">It is not another tool rollout.</p>
              </div>

              <p className="text-lg font-semibold text-navy" data-id="element-473">
                It is a disciplined way to find the next domino, remove what is
                blocking revenue, and make growth easier to carry.
              </p>
            </motion.div>
          </div>
        </motion.div>
      </section>

      {/* 4) We start wide, then go deep */}
      <section className="py-24 px-6 bg-paper grain" data-id="element-474">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={stagger} className="max-w-6xl mx-auto" data-id="element-475">
          <div className="max-w-3xl" data-id="element-476">
            <motion.div variants={fadeIn} className="mb-10" data-id="element-477">
              <h2 className="text-4xl md:text-5xl font-serif font-semibold text-navy mb-6 leading-tight" data-id="element-478">
                We start wide, then go deep.
              </h2>
            </motion.div>

            <motion.div variants={fadeIn} className="space-y-8" data-id="element-479">
              <div className="space-y-4 text-body text-text" data-id="element-480">
                <p data-id="element-481">
                  We take a fast pass across the journey end to end, then zoom
                  in where revenue is actually getting stuck, lost, or missed.
                </p>
                <p data-id="element-482">
                  That keeps us from treating the loudest symptom like the
                  source.
                </p>
                <p data-id="element-483">It also keeps us from fixing the wrong thing first.</p>
              </div>

              <div className="border-l-2 border-seaglass/60 pl-8 py-3" data-id="element-484">
                <p className="text-body text-navy font-semibold" data-id="element-485">
                  We are not looking for everything that could be improved.
                </p>
                <p className="text-body text-navy font-semibold mt-2" data-id="element-486">
                  We are looking for the next domino.
                </p>
              </div>

              <p className="text-lg text-navy font-semibold" data-id="element-487">
                Better diagnosis comes before better tools.
              </p>
            </motion.div>
          </div>
        </motion.div>
      </section>

      {/* 5) When this is a fit */}
      <section className="py-24 px-6 bg-paper" data-id="element-488">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={stagger} className="max-w-6xl mx-auto" data-id="element-489">
          <motion.div variants={fadeIn} className="mb-12 text-center md:text-left" data-id="element-490">
            <h2 className="text-4xl md:text-5xl font-serif font-semibold text-navy mb-6 leading-tight" data-id="element-491">
              When this is a fit
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-12 md:gap-16" data-id="element-492">
            <motion.div variants={fadeIn} data-id="element-493">
              <h3 className="text-xl font-semibold text-navy mb-6" data-id="element-494">
                This is usually a fit when…
              </h3>
              <ul className="space-y-3" data-id="element-495">
                {['growth is happening, but too much revenue still gets stuck, lost, or missed across the journey', 'problems keep getting created in the handoffs between sales, delivery, customer success, and operations', 'the same issue keeps showing up in different places under different names', 'pricing, packaging, churn, or expansion seem more connected than they first appear', 'leadership can feel the drag, but it is not clear what to fix first', 'better signal, cleaner handoffs, and better rules would matter more than another meeting or tool'].map((item, i) => <li key={i} className="flex items-start space-x-3" data-id="element-496">
                    <Check className="w-5 h-5 text-seaglass flex-shrink-0 mt-0.5" data-id="element-497" />
                    <span className="text-base text-text" data-id="element-498">{item}</span>
                  </li>)}
              </ul>
            </motion.div>

            <motion.div variants={fadeIn} className="md:pl-12 md:border-l md:border-seaglass/15" data-id="element-499">
              <h3 className="text-xl font-semibold text-navy mb-6" data-id="element-500">
                When it isn&apos;t…
              </h3>
              <ul className="space-y-3" data-id="element-501">
                {['you want someone to run operations for you long-term', 'the main problem is raw capacity, not journey friction or missed opportunity', 'you want generic AI consulting or team training', 'you already know exactly what needs to be implemented and just need execution', 'the issue is not meaningfully tied to how revenue moves through the business'].map((item, i) => <li key={i} className="flex items-start space-x-3" data-id="element-502">
                    <X className="w-5 h-5 text-seaglass flex-shrink-0 mt-0.5" data-id="element-503" />
                    <span className="text-base text-text" data-id="element-504">{item}</span>
                  </li>)}
              </ul>
            </motion.div>
          </div>
        </motion.div>
      </section>

      {/* 6) Start here */}
      <section className="py-24 px-6 bg-paper-dark grain" data-id="element-505">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={stagger} className="max-w-4xl mx-auto text-center md:text-left" data-id="element-506">
          <motion.div variants={fadeIn} className="mb-12" data-id="element-507">
            <h2 className="text-4xl md:text-5xl font-serif font-semibold text-navy mb-6 leading-tight" data-id="element-508">
              Start with an Honest Conversation
            </h2>
          </motion.div>

          <motion.div variants={fadeIn} className="bg-white border border-seaglass/20 shadow-sm p-8 md:p-12" data-id="element-509">
            <p className="text-body text-text mb-8" data-id="element-510">
              A 45-minute working conversation to pressure-test what you are
              seeing, where revenue may actually be getting stuck, lost, or
              missed, and whether there is a real issue worth diagnosing.
            </p>

            <div className="mb-10 text-left" data-id="element-511">
              <p className="text-sm font-semibold text-navy mb-4" data-id="element-512">
                You leave with:
              </p>
              <ul className="space-y-3" data-id="element-513">
                {['a clearer read on what is really going on', 'whether the issue looks structural, situational, or not worth touching right now', 'the next right step, including "keep pushing" if that is the truth'].map((item, i) => <li key={i} className="flex items-start space-x-3" data-id="element-514">
                    <Check className="w-5 h-5 text-seaglass flex-shrink-0 mt-0.5" data-id="element-515" />
                    <span className="text-base text-text" data-id="element-516">{item}</span>
                  </li>)}
              </ul>
            </div>

            <div className="text-left" data-id="element-517">
              <Link to="/honest-conversation" className="inline-flex items-center space-x-2 px-8 py-4 bg-sand border-2 border-sand text-navy font-medium hover:bg-white transition-all duration-300" data-id="element-518">
                <span data-id="element-519">Book an Honest Conversation</span>
                <ArrowRight className="w-5 h-5" data-id="element-520" />
              </Link>
              <p className="text-sm text-text-muted mt-4" data-id="element-521">
                45 minutes. No pitch. Just clarity.
              </p>
            </div>
          </motion.div>
        </motion.div>
      </section>
    </main>;
}